/**
 * Query keys del dominio Estadísticas avanzadas.
 *
 * Keys consistentes y serializables (`docs/frontend_conventions.md`, Datos). Los
 * analytics filtran por la sede activa (el backend lo hace vía `X-Sede-Id`), así que
 * cada key incluye la sede para **cachear por sede**: al cambiar de sede, las
 * métricas se recargan (clave distinta) en vez de mostrar las de la sede anterior,
 * igual que el dashboard. Las series que aceptan período lo incluyen también en su
 * key (cambiar el período pide la serie nueva sin perder la anterior en caché).
 */
import type { OrdersPeriod, RevenuePeriod, SellerPeriod } from "../schemas";

/** Raíz de todas las queries de estadísticas (para invalidación amplia). */
export const STATS_ROOT_KEY = ["stats"] as const;

/** Key de la efectividad de entregas, segmentada por sede activa. */
export function effectivenessQueryKey(sedeId: number | null) {
  return ["stats", "effectiveness", { sede: sedeId }] as const;
}

/** Key de las órdenes por día, segmentada por sede activa. */
export function dailyOrdersQueryKey(sedeId: number | null) {
  return ["stats", "daily_orders", { sede: sedeId }] as const;
}

/** Key de los tiempos creación->entrega, segmentada por sede activa. */
export function ordersTimingQueryKey(sedeId: number | null) {
  return ["stats", "orders_timing", { sede: sedeId }] as const;
}

/** Key de la serie de cantidad de órdenes, segmentada por sede activa y período. */
export function ordersChartQueryKey(
  sedeId: number | null,
  period: OrdersPeriod,
) {
  return ["stats", "orders_chart", { sede: sedeId, period }] as const;
}

/** Key de la serie de ingresos, segmentada por sede activa y período. */
export function revenueChartQueryKey(
  sedeId: number | null,
  period: RevenuePeriod,
) {
  return ["stats", "revenue_chart", { sede: sedeId, period }] as const;
}

/** Key de las ventas por vendedor, segmentada por sede activa y período. */
export function salesBySellerQueryKey(
  sedeId: number | null,
  period: SellerPeriod,
) {
  return ["stats", "sales_by_seller", { sede: sedeId, period }] as const;
}

/** Key de los pedidos de láminas recibidos, segmentada por sede activa y período. */
export function pedidosLaminasQueryKey(
  sedeId: number | null,
  period: OrdersPeriod,
) {
  return ["stats", "pedidos_laminas", { sede: sedeId, period }] as const;
}
